import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { Card } from "reactstrap";
import PageHeader from "../../components/PageHeader";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import StandardTable from "../../ui/components/StandardTable";
import Pagination from "../../components/utils/Pagination";
import EmptyState from "../../components/EmptyState";
import { InlineLoader } from "../../components/loader/CharacterizationLoader";
import { PeriodFilterModal } from "../../ui/components/filters";
import { toast } from "react-toastify";
import { getConfrontations } from "../../services/confrontation";
import {
  mapApiDocumentToListRow,
  formatConfrontationParentCategoryLabel,
} from "./confrontationMappers";
import {
  computeListPeriodSummary,
  formatPercentValue,
  getLocaleForLanguage,
} from "./confrontationMetrics";
import HighlightCard from "../../ui/components/HighlightCard";
import countIcon from "../../static/assets/dashboard-waste-analyzed.svg";
import averageIcon from "../../static/assets/dashboard-sorting-variation.svg";
import bestIcon from "../../static/assets/dashboard-sorting-previous.svg";
import worstIcon from "../../static/assets/dashboard-sorting-error.svg";

const RECORD_PER_PAGE = 10;
/** Rolling window when user has not applied a custom range from the modal */
const ROLLING_MONTHS_FALLBACK = 6;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatWeight = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  return `${parsed.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} kg`;
};

const ConfrontationList = () => {
  const history = useHistory();
  const token = Cookies.get("userToken");
  const { t, i18n } = useTranslation();
  const locale = getLocaleForLanguage(i18n.language);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  /** null = no server-side date filter (all confrontations for the corporation) */
  const [periodFilter, setPeriodFilter] = useState(null);
  const [periodDisplayLabel, setPeriodDisplayLabel] = useState("");
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [periodEndDate, setPeriodEndDate] = useState(null);
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(RECORD_PER_PAGE);
  const [resultPage, setResultPage] = useState(0);

  const { periodMaterialStart, periodMaterialStop } = useMemo(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (periodStartDate && periodEndDate) {
      const start = new Date(periodStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(periodEndDate);
      end.setHours(23, 59, 59, 999);
      return {
        periodMaterialStart: start.toISOString(),
        periodMaterialStop: end.toISOString(),
      };
    }
    if (periodFilter == null) {
      return {
        periodMaterialStart: undefined,
        periodMaterialStop: undefined,
      };
    }
    const months = Number(periodFilter);
    const startDate = new Date(now);
    startDate.setMonth(
      startDate.getMonth() -
        Math.ceil(
          !Number.isNaN(months) && months > 0
            ? months
            : ROLLING_MONTHS_FALLBACK,
        ),
    );
    startDate.setHours(0, 0, 0, 0);
    return {
      periodMaterialStart: startDate.toISOString(),
      periodMaterialStop: now.toISOString(),
    };
  }, [periodFilter, periodStartDate, periodEndDate]);

  const fetchConfrontations = useCallback(
    async () => {
      if (!token) return;
      setIsLoading(true);
      try {
        const response = await getConfrontations({
          start: periodMaterialStart,
          stop: periodMaterialStop,
          onlySnapshots: true,
        });
        const list = Array.isArray(response.data?.confrontations)
          ? response.data.confrontations
          : [];
        const mapped = list.map(mapApiDocumentToListRow).filter(Boolean);
        setRows(mapped);
      } catch (error) {
        console.error("Error fetching confrontations:", error);
        const apiMsg = error.response?.data?.error;
        toast.error(
          typeof apiMsg === "string" && apiMsg
            ? apiMsg
            : t("Could not load confrontations"),
        );
        setRows([]);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line
    [token, periodMaterialStart, periodMaterialStop],
  );

  useEffect(() => {
    fetchConfrontations();
  }, [fetchConfrontations]);

  const filteredRows = useMemo(() => {
    const searchLower = searchValue.trim().toLowerCase();
    if (!searchLower) return rows;
    return rows.filter(
      (row) =>
        row.confrontationLabel?.toLowerCase().includes(searchLower) ||
        row.flow?.toLowerCase().includes(searchLower),
    );
  }, [rows, searchValue]);

  /** Same order as backend: characterizationDate descending, then created confrontation date */
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const charA = a.characterizationDate
        ? new Date(a.characterizationDate).getTime()
        : 0;
      const charB = b.characterizationDate
        ? new Date(b.characterizationDate).getTime()
        : 0;
      if (charB !== charA) return charB - charA;
      const confA = a.confrontationDate
        ? new Date(a.confrontationDate).getTime()
        : 0;
      const confB = b.confrontationDate
        ? new Date(b.confrontationDate).getTime()
        : 0;
      return confB - confA;
    });
  }, [filteredRows]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * recordPerPage;
    return sortedRows.slice(start, start + recordPerPage);
  }, [currentPage, sortedRows, recordPerPage]);

  useEffect(() => {
    setResultPage(Math.ceil(sortedRows.length / recordPerPage));
  }, [sortedRows.length, recordPerPage]);

  const hasActiveFilters =
    Boolean(searchValue.trim()) ||
    periodStartDate != null ||
    periodFilter != null;

  const showEmptyState = !isLoading && rows.length === 0 && !hasActiveFilters;

  const periodSummary = useMemo(
    () => computeListPeriodSummary(sortedRows),
    [sortedRows],
  );

  const showListHighlights = !isLoading && sortedRows.length > 0;
  const bestWmae = formatPercentValue(periodSummary.bestWmae, locale);
  const worstWmae = formatPercentValue(periodSummary.worstWmae, locale);

  const handleRowClick = (row) => {
    history.push(`/confrontation/${row.id}`, { confrontation: row });
  };

  if (!token) {
    return <Redirect to={{ pathname: "/login" }} />;
  }

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("Confrontations")}
        helpButton={<HowItWorksButton topic="confrontations" />}
      />
      <div className="settings-content-wrapper">
        <div className="dashboard-kpi-panel">
          {showListHighlights ? (
            <div className="dashboard-volume-row">
              <HighlightCard
                iconSrc={countIcon}
                title={t("confrontation.count")}
                value={String(periodSummary.count)}
              />
              <HighlightCard
                iconSrc={averageIcon}
                title={t("confrontation.averageDeviation")}
                hint={t("confrontation.wmaeColumnHint")}
                value={
                  formatPercentValue(periodSummary.averageWmae, locale) || "—"
                }
              />
              <HighlightCard
                iconSrc={bestIcon}
                title={t("confrontation.bestWmae")}
                value={bestWmae || "—"}
              />
              <HighlightCard
                iconSrc={worstIcon}
                title={t("confrontation.worstWmae")}
                value={worstWmae || "—"}
              />
            </div>
          ) : null}
          <div className="settings-list-shell">
            <Card className="table border-0 shadow-none rounded-lg">
              <StandardTable
                fluidColCount={showEmptyState ? 1 : 5}
                fixedColWidth={0}
                containerClassName={
                  showEmptyState
                    ? "table-standard-container--list-empty"
                    : undefined
                }
                columnAlignments={
                  showEmptyState
                    ? ["left"]
                    : ["left", "left", "left", "left", "right"]
                }
                topBar={
                  showEmptyState ? null : (
                    <section className="table-standard-top-bar">
                      <div className="table-standard-top-bar-inner">
                        <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                          <div className="search-input-wrapper">
                            <i
                              className="fas fa-search search-input-icon"
                              aria-hidden
                            />
                            <input
                              type="text"
                              className="search-input"
                              value={searchValue}
                              placeholder={t("Search for a characterization")}
                              onChange={(event) => {
                                setSearchValue(event.target.value);
                                setCurrentPage(1);
                              }}
                            />
                          </div>
                          <div style={{ position: "relative" }}>
                            <button
                              type="button"
                              className="search-input"
                              style={{
                                padding: "10px 16px",
                                cursor: "pointer",
                                textAlign: "left",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                border: "1.5px solid #e0e0e0",
                                backgroundColor: "#fff",
                                height: "40px",
                                whiteSpace: "nowrap",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsPeriodModalOpen(true);
                              }}
                            >
                              <span>
                                {t("Period")} :{" "}
                                <span style={{ fontWeight: 600 }}>
                                  {periodDisplayLabel ||
                                    t("Since the beginning")}
                                </span>
                              </span>
                              <i
                                className="fas fa-chevron-down"
                                style={{ fontSize: "12px", flexShrink: 0 }}
                                aria-hidden
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </section>
                  )
                }
                header={
                  showEmptyState ? null : (
                    <div className="table-standard-header">
                      <div className="table-standard-header-cell">
                        {t("Characterization date")}
                      </div>
                      <div className="table-standard-header-cell">
                        {t("Confrontation date")}
                      </div>
                      <div className="table-standard-header-cell">
                        {t("Weight")}
                      </div>
                      <div className="table-standard-header-cell">
                        {t("Flux")}
                      </div>
                      <div
                        className="table-standard-header-cell"
                        title={t("confrontation.wmaeColumnHint")}
                      >
                        {t("confrontation.wmaeColumn")}
                      </div>
                    </div>
                  )
                }
                bottom={
                  !isLoading && sortedRows.length > 0 ? (
                    <div className="table-standard-bottom table-pagination-bottom">
                      <Pagination
                        totalEntry={sortedRows.length}
                        recordPerPage={recordPerPage}
                        setRecordPerPage={setRecordPerPage}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        resultPage={resultPage}
                        setResultPage={setResultPage}
                      />
                    </div>
                  ) : null
                }
              >
                {isLoading ? (
                  <div className="table-standard-row">
                    <div
                      className="table-standard-row-cell justify-center min-h-[200px]"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <InlineLoader height="200px" />
                    </div>
                  </div>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row) => (
                    <div
                      className="table-standard-row cursor-pointer"
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                    >
                      <div
                        className="table-standard-row-cell"
                        data-label={t("Characterization date")}
                      >
                        {formatDateTime(row.characterizationDate)}
                      </div>
                      <div
                        className="table-standard-row-cell"
                        data-label={t("Confrontation date")}
                      >
                        {formatDateTime(row.confrontationDate)}
                      </div>
                      <div
                        className="table-standard-row-cell"
                        data-label={t("Total weight")}
                      >
                        {formatWeight(row.totalWeight)}
                      </div>
                      <div
                        className="table-standard-row-cell"
                        data-label={t("Flux")}
                      >
                        {formatConfrontationParentCategoryLabel(row.flow, t)}
                      </div>
                      <div
                        className="table-standard-row-cell"
                        data-label={t("confrontation.wmaeColumn")}
                        title={t("confrontation.wmaeColumnHint")}
                      >
                        {formatPercentValue(row.wmae, locale) || "-"}
                      </div>
                    </div>
                  ))
                ) : hasActiveFilters ? (
                  <div className="table-standard-row table-standard-row--empty">
                    <div
                      className="table-standard-row-cell"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      {t("No results match your search")}
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    title={t("Confrontations")}
                    description={t("No confrontation available")}
                  />
                )}
              </StandardTable>
            </Card>
          </div>
        </div>
      </div>
      <PeriodFilterModal
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        onApply={(months, label, startDate, endDate) => {
          setPeriodFilter(months ?? null);
          setPeriodDisplayLabel(label || "");
          setPeriodStartDate(startDate ?? null);
          setPeriodEndDate(endDate ?? null);
          setCurrentPage(1);
        }}
        currentPeriodFilter={periodFilter}
        currentPeriodStart={periodStartDate}
        currentPeriodEnd={periodEndDate}
      />
    </div>
  );
};

export default ConfrontationList;
